import type { HelpSection } from '../helpContent';

const ALL: HelpSection['who'] = ['employee', 'lead', 'manager', 'admin', 'hr'];
/** the employee tabs: managers, admins and HR on an employee list get these too (helpFor) */
const EMP: HelpSection['who'] = ['employee', 'lead'];
const STORE: HelpSection['who'] = ['lead', 'manager', 'admin', 'hr'];

export const PHONE: HelpSection[] = [
  {
    id: 'signing-in', group: 'Signing in', title: 'Signing in and your PIN', who: ALL, where: ['phone', 'desk'],
    body: `
Everyone signs in with their own mobile number. Open **mywunderland.com** on your phone or computer.

### The first time on a phone or computer
1. Type your mobile number and press **Next**.
2. Type the 6-digit code. It's texted to you and works for 10 minutes. If you said no to texts, ask a manager for a sign-in code (it works for 15 minutes).
3. Pick your PIN: **4 digits** for employees and shift leads, **6 digits** for managers, admins and HR. Easy ones like 1111 or 1234 aren't allowed.

### After that
Type your number and your PIN. No text is needed. Your phone stays signed in (a year for employees and leads, 30 days for managers, admins and HR), until you press **Sign out**.

If you're made a manager, the app asks you to pick a new 6-digit PIN the next time you sign in.

### Forgot your PIN?
Press **Forgot PIN?** on the PIN screen. You get a new code (by text, or from a manager), then you pick a new PIN.

After 5 wrong PINs your sign-in is locked. Ask a manager to reset your PIN on the Employees page, then sign in again.

> Your PIN is yours alone. Never share it. Managers never need to know it. If you're a manager or shift lead, the same PIN also unlocks the store office computer and the counter screen.

### "Can we text you?"
The first time you sign in, people on a store's schedule are asked whether we can text you your schedules, message notices and sign-in codes. You don't have to agree to use the app. To say yes, read the terms and tick the box. You can change your answer any time on the Today screen.
`,
  },
  {
    id: 'phone-today', group: 'Your schedule', title: 'Today', who: EMP, where: ['phone'], pages: ['phone:today'],
    body: `
**Today** is the first screen you see.

- **Your shift**: your times, where you work and your paid hours.
- **Breaks**: your breaks and lunch, for example "Break 2:00p–2:15p", "Lunch 4:00p–4:30p (unpaid)".
- **Where you're stationed**: your station each hour, once your manager has created today's rotation.
- Not working today: it shows **You're off today** and your next shift.

Notices also show here:
- your manager answered a time-off request, or changed your availability (each with a **Dismiss** button),
- your shift was changed after it was posted ("was 12:00p–8:00p"),
- someone needs a shift covered ([[phone-cover]]).

> Schedules can change, even on the day. Check the app before each shift.

Links at the bottom take you to new messages and time-off requests that are still waiting.
`,
  },
  {
    id: 'phone-week', group: 'Your schedule', title: 'Your week', who: EMP, where: ['phone'], pages: ['phone:week'],
    body: `
**Week** shows your shifts for each day of the week, your breaks and your total hours. If more than one week has been posted, tap the week buttons at the top to switch.

You only see weeks your manager has posted. A day marked **Updated · was …** was changed after the week was posted, and shows what it used to be.
`,
  },
  {
    id: 'phone-availability', group: 'Your schedule', title: 'Your availability', who: EMP, where: ['phone'], pages: ['phone:avail'],
    body: `
**Availability** tells your managers when you can work. It repeats every week, and they see it while they build the schedule. For a one-off day off, use [[phone-timeoff|Time off]] instead.

1. Tap a day.
2. Choose **All day**, **Only some hours** (and pick from and until) or **Not available**.
3. Optional: tick **Preferred hours** to say which hours you'd like most. They're not guaranteed.
4. Press **Save**.

**Most I want to work each week** sets the most hours and days you'd like. Your managers get a warning if a schedule goes over it.

### When you limit your availability
If you're giving fewer hours than before, a notice explains that we'll do our best to schedule around them, but it may mean fewer hours. Talk to your manager first about big changes. If it's an accommodation for a medical condition, disability or religious practice, contact HR directly. Tick "I understand…" and press **Save preferences**, or **Message my manager** to talk first.

If a manager changes your availability, you'll see a notice saying who changed it and when.
`,
  },
  {
    id: 'phone-timeoff', group: 'Your schedule', title: 'Asking for time off', who: EMP, where: ['phone'], pages: ['phone:timeoff'],
    body: `
1. Open **Time off**.
2. Pick the **First day** (and the **Last day** if it's more than one).
3. Leave **All day** ticked, or untick it and pick the hours.
4. Add a note for your manager if you like, then press **Send request**.

Time off you ask for here is unpaid. If you're out sick, tell your manager, and they'll record it as sick pay.

Your requests are listed under **My requests**. When a manager answers, you'll see a notice on Today and a number on the Time off tab. You can **Cancel** a request that's waiting or approved.
`,
  },
  {
    id: 'phone-messages', group: 'Your schedule', title: 'Messages', who: EMP, where: ['phone'], pages: ['phone:messages'],
    body: `
Use **Messages** to write to your managers or to HR. Don't reply to texts from Wunderland: those replies aren't read.

- **My managers**: under **To**, pick **All Salem managers** or one manager. Each is its own conversation. A message to one manager is private to that manager (and admins).
- **HR**: a private conversation only HR can read. Your managers can't see it.

Type your message and press **Send message**. Replies show here. If you get texts, you'll usually also get a text telling you there's a new message.
`,
  },
  {
    id: 'phone-texts', group: 'Your schedule', title: 'Text alerts on or off', who: EMP, where: ['phone'],
    body: `
- **Texts off**: a "Text alerts are off" box shows near the top of Today. Press **Turn on texts**, read the terms, tick the box and press **Yes, text me**.
- **Texts on**: a small grey line at the bottom of Today says "Text alerts are on · change". Tap **change** to turn them off.

If you ever texted STOP, also text START to our number once after turning texts back on. With texts off you won't get schedule texts, message notices or sign-in codes, so check the app before your shifts.
`,
  },
  {
    id: 'phone-cover', group: 'Your schedule', title: 'Covering an open shift', who: EMP, where: ['phone'],
    body: `
When someone has to leave, your manager may text people who are off to ask if they can cover. The open shift shows as a card on your **Today** screen.

- Tap **I can cover it** if you can. There's no "no" button, so if you can't, just leave it.
- Your manager sees who said yes and gives the shift to one person. If it's you, you get a text ("You're covering 4:00p–8:00p today. Thanks!") and it shows on your schedule.
- If someone else got it, the card says **Shift covered**.
`,
  },
  {
    id: 'store-tab', group: 'For leads and managers', title: 'The Store tab', who: STORE, where: ['phone'], pages: ['phone:store'],
    body: `
Managers, shift leads, admins and HR get a **Store** tab on their phone. It refreshes every minute.

- Counts of who's **on the clock**, **on break** and **still to come in**.
- **On break now** and **Working now**, with when each person's break or shift ends.
- **Staffing this hour and next** (managers, admins and HR): scheduled against needed, green when it matches, yellow when short, red when over.

### Managers and admins can also
- **Mid-day change…**: mark someone who left early, called out, arrived late or was called in. Texting people to cover is done on the desktop.
- **Time-off requests waiting**: **Approve**, **Approve as sick pay** or **Deny**. Your own requests aren't listed. Another manager answers them.
- **This week's schedule**: **Post week** or **Post changes** for this week. Later weeks are built and posted on the desktop pages.
- **Messages from employees**: read and reply to the latest conversations.

Shift leads see the store's day (view only). HR sees every store but can't change anything.

Managers, admins and HR: **Open the desktop pages** at the bottom switches this phone or tablet to the full desktop pages. On a small screen, **Phone view** in the top bar switches back.
`,
  },
];
